import { DEMO } from "./demoGuard";
/** Mirror of the Electron preload bridge (apps/desktop/src/preload.ts). Undefined in a browser. */
export type UpdateStatus = { state: "idle" | "unconfigured" | "checking" | "available" | "not-available" | "downloading" | "downloaded" | "error"; version?: string; percent?: number; message?: string;
  /** Staff asked for this update: it installs and restarts by itself once downloaded (after any payment, order or print finishes). */
  autoInstall?: boolean };
export type UpdateLogEntry = { at: string; event: string; version?: string; detail?: string };
export type UpdaterState = { status: UpdateStatus; currentVersion: string; feedUrl: string; channel: UpdateChannel; history: UpdateLogEntry[]; signatureEnforced: boolean };
export type UpdateChannel = "stable" | "beta" | "staging";
export type BackupStatus = { localFolder: string; externalFolder: string; lastBackupAt: string; externalError: string };
export type PrintOutcome = { state: "printed" | "failed" | "unknown"; message?: string };
export type DesktopApi = {
  platform: "electron";
  updater: {
    getState(): Promise<UpdaterState>;
    configure(input: { feedUrl: string; channel: UpdateChannel }): Promise<{ ok: boolean; reason?: string }>;
    check(): Promise<UpdateStatus>;
    cancel(): Promise<void>;
    install(): Promise<{ accepted: boolean; reason?: string }>;
    setBusyState(state: { activePayment: boolean; unsavedOrder: boolean; printInProgress: boolean }): void;
    onStatus(listener: (status: UpdateStatus) => void): () => void;
  };
  printer: {
    list(): Promise<{ name: string; isDefault: boolean }[]>;
    printHtml(job: { deviceName: string; widthMm: 58 | 80; html: string }): Promise<PrintOutcome>;
    printRaw(job: { host: string; port: number; dataBase64: string }): Promise<PrintOutcome>;
  };
  backup: {
    writeAuto(json: string): Promise<{ path: string }>;
    exportFile(json: string, suggestedName: string): Promise<{ saved: boolean; path?: string }>;
    importFile(): Promise<string | null>;
    status(): Promise<BackupStatus>;
    setFolder(choose: boolean): Promise<BackupStatus>;
  };
  credentials: {
    write(input: { login: string; password: string }): Promise<{ path: string }>;
    status(): Promise<{ path: string; exists: boolean }>;
    reveal(): Promise<void>;
    remove(): Promise<void>;
  };
};
declare global { interface Window { mangoMoons?: DesktopApi } }
/** Off in the demo: it must not print, back up, or install updates on this computer. */
export const desktop: DesktopApi | undefined = DEMO ? undefined : window.mangoMoons;

const busy = { activePayment: false, unsavedOrder: false, printInProgress: false };
/** Tells the desktop shell what must not be interrupted by an update restart. */
export function setBusy(patch: Partial<typeof busy>) {
  Object.assign(busy, patch);
  desktop?.updater.setBusyState({ ...busy });
}
